import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { PipelineStage } from '../../types';
interface StageBadgeProps {
  stage?: PipelineStage;
  /** Estados disponibles: si se pasan, la etiqueta abre un desplegable. */
  stages?: PipelineStage[];
  onChange?: (stageId: string) => void;
  className?: string;
}

/**
 * Etiqueta de estado con el color configurado por la empresa.
 * Si recibe `stages` y `onChange`, permite cambiar el estado con un clic.
 */
export function StageBadge({
  stage,
  stages,
  onChange,
  className = ''
}: StageBadgeProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);
  const color = stage?.color ?? '#64748B';
  const name = stage?.name ?? 'Sin estado';
  const pill = <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${className}`} style={{
    backgroundColor: `${color}1A`,
    color
  }} data-id="element-220" data-mp="3g92o8zupwns6">
      <span className="h-1.5 w-1.5 flex-none rounded-full" style={{
      backgroundColor: color
    }} aria-hidden="true" data-id="element-221" data-mp="37fzcqx3mul06" />
      {name}
      {stages && <ChevronDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-222" data-mp="2shxacq8c85ki" />}
    </span>;
  if (!stages || !onChange) return pill;
  return <div ref={ref} className="relative inline-block" data-id="element-223" data-mp="2xfyulehi2axk">
      <button type="button" onClick={e => {
      e.stopPropagation();
      setOpen(v => !v);
    }} aria-haspopup="listbox" aria-expanded={open} aria-label={`Cambiar estado, actualmente ${name}`} className="min-h-[32px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-224" data-mp="2jjvc2ifznr0x">
        {pill}
      </button>
      {open && <ul role="listbox" className="absolute left-0 top-full z-40 mt-1.5 max-h-72 w-56 overflow-y-auto rounded-xl border border-border bg-background p-1 shadow-lg" data-id="element-225" data-mp="kbn17zxcut5a">
          {stages.filter(s => s.active).map(option => <li key={option.id} data-id="element-226" data-mp="baotp7npk3hy">
                <button type="button" role="option" aria-selected={option.id === stage?.id} onClick={e => {
          e.stopPropagation();
          onChange(option.id);
          setOpen(false);
        }} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-surface ${option.id === stage?.id ? 'font-semibold text-foreground' : ''}`} data-id="element-227" data-mp="bbq7hjw3z43d">
                  <span className="h-2 w-2 flex-none rounded-full" style={{
            backgroundColor: option.color
          }} aria-hidden="true" data-id="element-228" data-mp="u8rgdfss9ocl" />
                  {option.name}
                </button>
              </li>)}
        </ul>}
    </div>;
}